/**
 * Pattern C write gate: a client-only connected address is not enough.
 * Privileged inserts/uploads require a valid signed { didId, eligibleUntil }
 * claim and a fresh Global DID eligibility ALLOW.
 */

import type { DidEligibility, DidReader } from './didEligibility';
import { verifyGlobalDidAccess } from './didEligibility';
import type { Web3Claim } from './siweSession';
import { isClaimValid, recoveredSignerMatches } from './siweSession';

export class Web3AuthError extends Error {
  constructor(
    message: string,
    readonly code:
      | 'not_signed_in'
      | 'claim_expired'
      | 'bad_signature'
      | 'did_ineligible'
      | 'wrong_wallet'
  ) {
    super(message);
    this.name = 'Web3AuthError';
  }
}

export async function authorizeApplicationWrite(opts: {
  claim: Web3Claim | null | undefined;
  connectedAddress?: string | null;
  reader: DidReader;
  recoverSigner: (message: string, signature: string) => string;
  now?: number;
  checkNewFlow?: (didId: number) => Promise<{
    isNewFlowKyc: boolean;
    isCountryBlocked: boolean;
  }>;
}): Promise<{ address: string; didId: number; eligibility: DidEligibility }> {
  const now = opts.now ?? Date.now();
  if (!opts.claim) {
    throw new Web3AuthError(
      'Sign in with your wallet and Apertum Global DID to continue.',
      'not_signed_in'
    );
  }
  if (!isClaimValid(opts.claim, now)) {
    throw new Web3AuthError(
      'Your sign-in session expired. Sign in again.',
      'claim_expired'
    );
  }

  let recovered: string;
  try {
    recovered = opts.recoverSigner(opts.claim.message, opts.claim.signature);
  } catch {
    throw new Web3AuthError('Could not verify your sign-in signature.', 'bad_signature');
  }
  if (!recoveredSignerMatches(recovered, opts.claim.address)) {
    throw new Web3AuthError(
      'Sign-in signature does not match this wallet.',
      'bad_signature'
    );
  }
  if (
    opts.connectedAddress &&
    opts.connectedAddress.toLowerCase() !== opts.claim.address.toLowerCase()
  ) {
    throw new Web3AuthError(
      'Connected wallet does not match the signed session. Sign in again.',
      'wrong_wallet'
    );
  }

  if (opts.claim.kind === 'admin') {
    throw new Web3AuthError(
      'Admin sessions cannot submit applications. Sign in with a wallet that holds a Global DID.',
      'did_ineligible'
    );
  }

  const eligibility = await verifyGlobalDidAccess({
    wallet: opts.claim.address,
    didId: opts.claim.didId,
    reader: opts.reader,
    checkNewFlow: opts.checkNewFlow,
  });
  if (!eligibility.eligible || eligibility.didId == null) {
    throw new Web3AuthError(
      eligibility.message || 'This wallet is not eligible with a Global DID.',
      'did_ineligible'
    );
  }

  return {
    address: opts.claim.address,
    didId: eligibility.didId,
    eligibility,
  };
}
